/**
 * Tentpole Calendar tab — the master content calendar, three views:
 *
 *   By tentpole — tentpole directory (every moment, including empty ones),
 *                 plan sections per tentpole, creator-added custom moments,
 *                 orphans.
 *   By date     — agenda list grouped chronologically (AgendaView).
 *   Month       — CSS-grid month calendar with day chips (MonthGrid).
 *
 * Above every view: a two-row toolbar (how to look — view and timeframe —
 * then what to narrow by) and, while submissions wait, a one-line review
 * banner whose queue table opens on demand.
 *
 * Fully controlled (filters/viewMode live in SalesDashboard so they survive
 * tab switches); the month pager and the queue's open state are local here
 * (they reset with the dashboard tab, survive view toggles). The
 * "✦ Custom moments" tentpole-select sentinel ('__custom__') narrows to
 * entries with a customLabel.
 */

import React, { useMemo, useState } from 'react';
import { Button, Card, EmptyState, Spinner, StatusPill, Tabs, tableStyles } from '../../ui';
import FilterChipGroup from '../FilterChipGroup';
import WidgetHeader from '../WidgetHeader';
import { bannerStyle } from '../MediaPlans/formStyles';
import EntryRow, { MomentChip } from './EntryRow';
import TimingPill from './TimingPill';
import TentpoleDirectory from './TentpoleDirectory';
import AgendaView from './AgendaView';
import MonthGrid from './MonthGrid';
import {
  CalendarEntry,
  CalendarEntryStatus,
  Tentpole,
  TentpoleCategory,
  TENTPOLE_CATEGORIES,
  TENTPOLE_CATEGORY_LABELS,
} from './types';
import {
  entryDisplayParts,
  fmtDate,
  fmtDaysAgo,
  fmtPlannedRange,
  fmtTentpoleWindow,
  isUpcomingEntry,
  isUpcomingTentpole,
  monthKeyOf,
  planMetaParts,
  plansWord,
  targetChannelLabels,
  tentpoleLabel,
  todayYmd,
} from './shared';

export type CalendarViewMode = 'tentpole' | 'agenda' | 'month';

export interface CalendarFiltersState {
  status: '' | CalendarEntryStatus;
  /** '' | tentpole slug | '__custom__' (custom-moment entries only). */
  tentpoleSlug: string;
  creatorSlug: string;
  category: '' | TentpoleCategory;
  timeframe: 'upcoming' | 'past' | 'all';
  viewMode: CalendarViewMode;
}

/** The moment-shaped filters (moment select + category) as one predicate —
 *  '__custom__' never matches a tentpole, an explicit pick wins over the
 *  category. Shared by month-grid bands and the tentpole sections so the two
 *  surfaces can't drift on what the filters mean. */
const matchesMomentFilters = (
  t: Tentpole,
  filters: Pick<CalendarFiltersState, 'tentpoleSlug' | 'category'>,
): boolean => {
  if (filters.tentpoleSlug === '__custom__') return false; // custom moments have no tentpole
  if (filters.tentpoleSlug) return t.slug === filters.tentpoleSlug;
  if (filters.category) return t.category === filters.category;
  return true;
};

const TIMEFRAME_OPTIONS: Array<{ value: CalendarFiltersState['timeframe']; label: string }> = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All time' },
];

interface CalendarTabProps {
  tentpoles: Tentpole[];
  entries: CalendarEntry[];
  loading: boolean;
  /** The last calendar load failed; shown only while nothing has loaded. */
  loadError: string | null;
  onRetry: () => void;
  /** The general entries endpoint returned a continuation cursor. */
  hasMore: boolean;
  filters: CalendarFiltersState;
  onFiltersChange: (next: CalendarFiltersState) => void;
  pendingCount: number;
  reviewEntries: CalendarEntry[];
  reviewLoading: boolean;
  reviewHasMore: boolean;
  reviewLoadingMore: boolean;
  onLoadMoreReview: () => void;
  onLoadMoreEntries: () => void;
  loadingMoreEntries: boolean;
  canManage: boolean;
  onOpenEntry: (id: string) => void;
  onEditTentpole: (tentpole: Tentpole) => void;
  onAddEntryFor: (tentpoleSlug: string) => void;
  onAddTentpole: () => void;
}

const CalendarTab: React.FC<CalendarTabProps> = ({
  tentpoles,
  entries,
  loading,
  loadError,
  onRetry,
  hasMore,
  filters,
  onFiltersChange,
  pendingCount,
  reviewEntries,
  reviewLoading,
  reviewHasMore,
  reviewLoadingMore,
  onLoadMoreReview,
  onLoadMoreEntries,
  loadingMoreEntries,
  canManage,
  onOpenEntry,
  onEditTentpole,
  onAddEntryFor,
  onAddTentpole,
}) => {
  const [hoverId, setHoverId] = useState<string | null>(null);
  // The review queue opens from its banner; closed, the calendar starts at the top.
  const [queueOpen, setQueueOpen] = useState(false);
  // Month pager — survives view toggles (CalendarTab stays mounted); resetting
  // to the current month on dashboard-tab re-entry is deliberate.
  const [monthKey, setMonthKey] = useState<string>(() => monthKeyOf(todayYmd()));

  const statusOptions = useMemo(() => ([
    { value: '' as const, label: 'All' },
    { value: 'submitted' as const, label: pendingCount > 0 ? `Needs review (${pendingCount})` : 'Needs review' },
    { value: 'approved' as const, label: 'Approved' },
    { value: 'declined' as const, label: 'Declined' },
    { value: 'withdrawn' as const, label: 'Withdrawn' },
  ]), [pendingCount]);

  const creatorOptions = useMemo(() => {
    const bySlug = new Map<string, string>();
    for (const e of [...reviewEntries, ...entries]) {
      if (!bySlug.has(e.creatorSlug)) bySlug.set(e.creatorSlug, e.creatorLabel);
    }
    const options: Array<{ slug: string; label: string }> = [];
    bySlug.forEach((label, slug) => options.push({ slug, label }));
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [entries, reviewEntries]);

  const tentpoleBySlug = useMemo(() => new Map(tentpoles.map((t) => [t.slug, t])), [tentpoles]);

  // Category-grouped options for the tentpole select (80 flat options are unusable).
  const tentpolesByCategory = useMemo(() => {
    const map = new Map<TentpoleCategory, Tentpole[]>();
    tentpoles.forEach((t) => {
      const list = map.get(t.category) ?? [];
      list.push(t);
      map.set(t.category, list);
    });
    map.forEach((list) => list.sort((a, b) => a.startDate.localeCompare(b.startDate)));
    return map;
  }, [tentpoles]);

  // Submitted views come from the purpose-built oldest-first server queue,
  // never from the newest-first general calendar page.
  const entrySource = filters.status === 'submitted' ? reviewEntries : entries;
  const filteredEntries = useMemo(() => entrySource.filter((e) => {
    if (filters.status && e.status !== filters.status) return false;
    if (filters.creatorSlug && e.creatorSlug !== filters.creatorSlug) return false;
    if (filters.tentpoleSlug === '__custom__') {
      if (!e.customLabel) return false;
    } else if (filters.tentpoleSlug && e.tentpoleSlug !== filters.tentpoleSlug) return false;
    // An explicit pick wins over the category, as in matchesMomentFilters.
    if (filters.category && !filters.tentpoleSlug) {
      if (e.customLabel) return false; // custom moments are uncategorized
      const t = e.tentpoleSlug ? tentpoleBySlug.get(e.tentpoleSlug) : undefined;
      if (!t || t.category !== filters.category) return false;
    }
    return true;
  }), [entrySource, filters, tentpoleBySlug]);

  const entriesByTentpole = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const e of filteredEntries) {
      if (!e.tentpoleSlug) continue;
      const list = map.get(e.tentpoleSlug) ?? [];
      list.push(e);
      map.set(e.tentpoleSlug, list);
    }
    map.forEach((list) => list.sort((a, b) => a.plannedDate.localeCompare(b.plannedDate)));
    return map;
  }, [filteredEntries]);

  // slug → count for the directory (plain object, ES5-safe).
  const entryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    entriesByTentpole.forEach((list, slug) => { counts[slug] = list.length; });
    return counts;
  }, [entriesByTentpole]);

  // Custom-moment entries (customLabel set), timeframe-scoped.
  const customEntries = useMemo(
    () => filteredEntries
      .filter((e) => !!e.customLabel)
      .filter((e) => {
        if (filters.timeframe === 'upcoming') return isUpcomingEntry(e);
        if (filters.timeframe === 'past') return !isUpcomingEntry(e);
        return true;
      })
      .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate)),
    [filteredEntries, filters.timeframe],
  );

  // Entries whose tentpole no longer exists — grouped under "Other", never
  // dropped. Custom entries are NOT orphans (they never had a tentpole).
  const orphanedEntries = useMemo(
    () => filteredEntries
      .filter((e) => (e.tentpoleSlug ? !tentpoleBySlug.has(e.tentpoleSlug) : false))
      .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate)),
    [filteredEntries, tentpoleBySlug],
  );

  // Timeframe applied to entries themselves — feeds the agenda view.
  const timeframedEntries = useMemo(
    () => filteredEntries.filter((e) => {
      if (filters.timeframe === 'upcoming') return isUpcomingEntry(e);
      if (filters.timeframe === 'past') return !isUpcomingEntry(e);
      return true;
    }),
    [filteredEntries, filters.timeframe],
  );

  // Which entry-side filters are narrowing?
  const narrowing = !!(filters.status || filters.creatorSlug);

  // Tentpole bands for the month grid follow only the moment-shaped filters;
  // creator/status/timeframe filters never hide them — tentpoles are
  // creator-agnostic ambient context. MonthGrid applies the active +
  // month-overlap rules itself.
  const bandTentpoles = useMemo(
    () => tentpoles.filter((t) => matchesMomentFilters(t, filters)),
    [tentpoles, filters],
  );

  // Sections render only for tentpoles with matching entries — the directory
  // carries the full catalog (and the empty-tentpole "+ Entry" CTA).
  const visibleTentpoles = useMemo(() => tentpoles
    .filter((t) => {
      if (!matchesMomentFilters(t, filters)) return false;
      if (filters.tentpoleSlug) return true; // explicit pick wins, even when empty
      if (filters.timeframe === 'upcoming' && !isUpcomingTentpole(t)) return false;
      if (filters.timeframe === 'past' && isUpcomingTentpole(t)) return false;
      const matching = entriesByTentpole.get(t.slug)?.length ?? 0;
      return matching > 0;
    })
    .sort((a, b) => a.startDate.localeCompare(b.startDate)),
  [tentpoles, filters, entriesByTentpole]);

  if (loading && tentpoles.length === 0 && entries.length === 0) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;
  }

  // Empty because the load failed, not because this is a first run.
  if (loadError && tentpoles.length === 0 && entries.length === 0) {
    return (
      <div role="alert">
        <EmptyState
          title="Couldn't load the calendar"
          subtitle={loadError}
          brand={false}
          cta={<Button variant="primary" size="sm" onClick={onRetry}>Retry</Button>}
        />
      </div>
    );
  }

  if (tentpoles.length === 0 && entries.length === 0) {
    return (
      <EmptyState
        title="No tentpoles yet"
        subtitle="Tentpoles are the cultural moments the calendar is organized around — add the first one to start collecting creator plans."
        cta={canManage ? <Button variant="primary" size="sm" onClick={onAddTentpole}>+ Add tentpole</Button> : undefined}
      />
    );
  }

  const nothingInTentpoleView =
    visibleTentpoles.length === 0 && orphanedEntries.length === 0 && customEntries.length === 0;

  // Any narrowing beyond the view and timeframe; "Clear filters" resets it.
  const filtersActive = !!(filters.status || filters.tentpoleSlug || filters.category || filters.creatorSlug);
  const clearFilters = () => onFiltersChange({ ...filters, status: '', tentpoleSlug: '', category: '', creatorSlug: '' });
  // The queue arrives oldest first, so its first row has waited longest.
  const oldestWaiting = reviewEntries.length > 0 ? fmtDaysAgo(reviewEntries[0].createdAt) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Toolbar — first how to look (view, when), then what to narrow by. */}
      <div style={toolbarStyle}>
        <div style={toolbarRowStyle}>
          <Tabs<CalendarViewMode>
            value={filters.viewMode}
            onChange={(viewMode) => onFiltersChange({ ...filters, viewMode })}
            options={[
              { value: 'tentpole', label: 'By tentpole' },
              { value: 'agenda', label: 'By date' },
              { value: 'month', label: 'Month' },
            ]}
            variant="segmented"
            ariaLabel="Calendar view"
          />
          {filters.viewMode !== 'month' && (
            <div role="group" aria-label="When" style={filterGroupStyle}>
              <span style={groupLabelStyle}>When</span>
              <FilterChipGroup
                tone="soft"
                options={TIMEFRAME_OPTIONS}
                value={filters.timeframe}
                onChange={(timeframe) => onFiltersChange({ ...filters, timeframe })}
              />
            </div>
          )}
          {filtersActive && (
            <button type="button" style={clearFiltersStyle} onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
        <div style={toolbarRowStyle}>
          <div role="group" aria-label="Review status" style={filterGroupStyle}>
            <span style={groupLabelStyle}>Review</span>
            <FilterChipGroup
              tone="soft"
              options={statusOptions}
              value={filters.status}
              onChange={(status) => onFiltersChange({ ...filters, status })}
            />
          </div>
          <select
            value={filters.tentpoleSlug}
            onChange={(e) => onFiltersChange({ ...filters, tentpoleSlug: e.target.value })}
            style={filterSelectStyle(!!filters.tentpoleSlug)}
            aria-label="Filter by tentpole"
          >
            <option value="">All tentpoles</option>
            <option value="__custom__">✦ Custom moments</option>
            {TENTPOLE_CATEGORIES.map((cat) => {
              const group = tentpolesByCategory.get(cat);
              if (!group || group.length === 0) return null;
              return (
                <optgroup key={cat} label={TENTPOLE_CATEGORY_LABELS[cat]}>
                  {group.map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {tentpoleLabel(t)}{t.active ? '' : ' (inactive)'}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          <select
            value={filters.category}
            onChange={(e) => onFiltersChange({ ...filters, category: e.target.value as '' | TentpoleCategory })}
            style={filterSelectStyle(!!filters.category)}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {TENTPOLE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{TENTPOLE_CATEGORY_LABELS[cat]}</option>
            ))}
          </select>
          <select
            value={filters.creatorSlug}
            onChange={(e) => onFiltersChange({ ...filters, creatorSlug: e.target.value })}
            style={filterSelectStyle(!!filters.creatorSlug)}
            aria-label="Filter by creator"
          >
            <option value="">All creators</option>
            {creatorOptions.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Waiting submissions — one line above every view; the queue opens on demand. */}
      {pendingCount > 0 && !filters.status && (
        <div>
          <div
            style={{
              ...bannerStyle('warning'),
              alignItems: 'center',
              borderRadius: queueOpen ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)',
            }}
          >
            <span aria-hidden="true" style={reviewDotStyle} />
            <span>
              {pendingCount} {pendingCount === 1 ? 'plan needs' : 'plans need'} review
            </span>
            {oldestWaiting && (
              <span style={{ fontWeight: 500, color: 'var(--fg-2)' }}>· oldest submitted {oldestWaiting}</span>
            )}
            <Button
              size="sm"
              variant="subtle"
              aria-expanded={queueOpen}
              aria-controls="tentpole-review-queue"
              onClick={() => setQueueOpen((open) => !open)}
              style={{ marginLeft: 'auto' }}
            >
              {queueOpen ? 'Hide queue' : 'Review'}
            </Button>
          </div>
          {queueOpen && (
            <Card id="tentpole-review-queue" padding={16} style={reviewQueueCardStyle}>
              {reviewLoading && reviewEntries.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner size={18} /></div>
              ) : (
                <div style={tableStyles.wrapScroll}>
                  <table style={tableStyles.table}>
                    <thead>
                      <tr>
                        <th style={tableStyles.th}>Plan</th>
                        <th style={tableStyles.th}>Moment</th>
                        <th style={tableStyles.th}>Planned</th>
                        <th style={tableStyles.th}>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewEntries.map((entry) => {
                        const tentpole = entry.tentpoleSlug ? tentpoleBySlug.get(entry.tentpoleSlug) : undefined;
                        // Same anatomy as the calendar rows, so a reviewer can match a
                        // queue row to its row elsewhere by the same headline.
                        const channels = targetChannelLabels(entry);
                        return (
                          <tr
                            key={entry.id}
                            onClick={() => onOpenEntry(entry.id)}
                            onMouseEnter={() => setHoverId(`q-${entry.id}`)}
                            onMouseLeave={() => setHoverId(null)}
                            style={{
                              cursor: 'pointer',
                              background: hoverId === `q-${entry.id}` ? tableStyles.rowHoverBackground : 'transparent',
                            }}
                          >
                            <td style={tableStyles.td} title={channels.length > 2 ? channels.join(', ') : undefined}>
                              <div style={{ color: 'var(--fg-1)' }}>
                                <span style={{ fontWeight: 600 }}>{entryDisplayParts(entry).primary}</span>{' '}
                                <span style={{ marginLeft: 4 }}>{entry.title}</span>
                              </div>
                              <div style={queueSubStyle}>{planMetaParts(entry).join(' · ')}</div>
                              {entry.requiresManagerReview && (
                                <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--warning)', fontWeight: 700 }}>
                                  Manager scope decision required
                                </div>
                              )}
                            </td>
                            <td style={tableStyles.td}><MomentChip entry={entry} tentpole={tentpole} /></td>
                            <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>{fmtPlannedRange(entry)}</td>
                            <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                              {fmtDate(entry.createdAt)}
                              <div style={queueSubStyle}>{fmtDaysAgo(entry.createdAt)}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {reviewHasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
                  <Button size="sm" variant="subtle" onClick={onLoadMoreReview} disabled={reviewLoadingMore}>
                    {reviewLoadingMore ? 'Loading…' : 'Load more submissions'}
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* --- View: By tentpole ------------------------------------------------ */}
      {filters.viewMode === 'tentpole' && (
        <>
          <TentpoleDirectory
            tentpoles={tentpoles}
            entryCounts={entryCounts}
            timeframe={filters.timeframe}
            category={filters.category}
            canManage={canManage}
            onEditTentpole={onEditTentpole}
            onAddEntryFor={onAddEntryFor}
          />

          {/* The ✦ sentinel is excluded — the custom-moments card below carries its own empty hint. */}
          {nothingInTentpoleView &&
          (narrowing || (filters.tentpoleSlug && filters.tentpoleSlug !== '__custom__') || filters.category) ? (
            <EmptyState
              title="No plans match"
              subtitle="Try widening the timeframe or clearing a filter."
              brand={false}
            />
          ) : (
            visibleTentpoles.map((tentpole) => {
              const sectionEntries = entriesByTentpole.get(tentpole.slug) ?? [];
              return (
                <Card key={tentpole.slug}>
                  <WidgetHeader
                    title={tentpoleLabel(tentpole)}
                    subtitle={
                      <span style={sectionMetaStyle}>
                        <TimingPill tentpole={tentpole} />
                        <span>{fmtTentpoleWindow(tentpole)}</span>
                        <span aria-hidden="true">·</span>
                        <span>{plansWord(sectionEntries.length)}</span>
                      </span>
                    }
                    right={
                      <>
                        {!tentpole.active && <StatusPill variant="paused" size="sm" noDot>Inactive</StatusPill>}
                        {/* Internal is every tentpole's default; only the exception is labelled. */}
                        {tentpole.externalVisible && (
                          <span title="Marked for the client-facing calendar">
                            <StatusPill variant="approved" size="sm" noDot>External</StatusPill>
                          </span>
                        )}
                        {canManage && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => onEditTentpole(tentpole)}>Edit tentpole</Button>
                            <Button size="sm" variant="subtle" onClick={() => onAddEntryFor(tentpole.slug)}>+ Add plan</Button>
                          </>
                        )}
                      </>
                    }
                  />
                  {sectionEntries.length === 0 ? (
                    <div style={{ ...hintStyle, padding: '14px 0 2px' }}>
                      Nothing planned yet{canManage ? '. Add a plan, or wait for creators to submit one.' : '.'}
                    </div>
                  ) : (
                    <div style={{ marginTop: 10 }}>
                      {sectionEntries.map((e) => (
                        <EntryRow key={e.id} entry={e} showDate onOpen={onOpenEntry} />
                      ))}
                    </div>
                  )}
                </Card>
              );
            })
          )}

          {/* Creator-added custom moments */}
          {(customEntries.length > 0 || filters.tentpoleSlug === '__custom__') && (
            <Card>
              <WidgetHeader
                title="Creator-added moments"
                subtitle={`${plansWord(customEntries.length)} around moments that aren't tentpoles yet. Open one to make it a tentpole.`}
              />
              {customEntries.length === 0 ? (
                <div style={{ ...hintStyle, padding: '14px 0 2px' }}>No custom moments in this timeframe.</div>
              ) : (
                <div style={{ marginTop: 10 }}>
                  {customEntries.map((e) => (
                    <EntryRow key={e.id} entry={e} showDate showMoment onOpen={onOpenEntry} />
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Orphaned entries — tentpole was deleted */}
          {orphanedEntries.length > 0 && (
            <Card>
              <WidgetHeader
                title="Tentpole removed"
                subtitle="These plans' tentpole was deleted. Reassign them in Edit plan, or withdraw them."
              />
              <div style={{ marginTop: 10 }}>
                {orphanedEntries.map((e) => (
                  <EntryRow key={e.id} entry={e} showDate showMoment onOpen={onOpenEntry} />
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* --- View: By date ---------------------------------------------------- */}
      {filters.viewMode === 'agenda' && (
        <AgendaView
          entries={timeframedEntries}
          tentpoleBySlug={tentpoleBySlug}
          timeframe={filters.timeframe}
          onOpenEntry={onOpenEntry}
        />
      )}

      {/* --- View: Month ------------------------------------------------------ */}
      {filters.viewMode === 'month' && (
        <MonthGrid
          monthKey={monthKey}
          onMonthChange={setMonthKey}
          entries={filteredEntries}
          tentpoleBySlug={tentpoleBySlug}
          onOpenEntry={onOpenEntry}
          tentpoles={bandTentpoles}
          canManage={canManage}
          onEditTentpole={onEditTentpole}
          onAddEntryFor={onAddEntryFor}
          onShowTentpolePlans={(slug) => onFiltersChange({ ...filters, tentpoleSlug: slug, category: '', viewMode: 'tentpole' })}
        />
      )}

      {(filters.status === 'submitted' ? reviewHasMore : hasMore) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
          <span style={hintStyle}>
            {filters.status === 'submitted' ? 'More review submissions are available.' : 'More calendar history is available.'}
          </span>
          <Button
            size="sm"
            variant="subtle"
            onClick={filters.status === 'submitted' ? onLoadMoreReview : onLoadMoreEntries}
            disabled={filters.status === 'submitted' ? reviewLoadingMore : loadingMoreEntries}
          >
            {filters.status === 'submitted'
              ? reviewLoadingMore ? 'Loading…' : 'Load more submissions'
              : loadingMoreEntries ? 'Loading…' : 'Load older plans'}
          </Button>
        </div>
      )}
    </div>
  );
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const toolbarRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
};

const filterGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const groupLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  fontFamily: 'var(--font-sans)',
};

/** A text link, so it opts out of the global button hover lift. */
const clearFiltersStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '4px 2px',
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  transform: 'none',
  color: 'var(--accent)',
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
};

const selectStyle: React.CSSProperties = {
  padding: '7px 10px',
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  color: 'var(--fg-2)',
  background: 'var(--bg-surface-1)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  maxWidth: 240,
};

/** An applied filter is tinted, so what's narrowing the view shows at a glance. */
const filterSelectStyle = (active: boolean): React.CSSProperties => (active
  ? { ...selectStyle, color: 'var(--accent-soft-fg)', background: 'var(--accent-soft)', border: '1px solid var(--accent)' }
  : selectStyle);

const reviewDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: 'var(--warning)',
  flex: 'none',
};

/** Hangs off the banner: square top corners, the Card's own radius below. */
const reviewQueueCardStyle: React.CSSProperties = {
  borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
};

const queueSubStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 12,
  fontWeight: 400,
  color: 'var(--fg-3)',
};

const sectionMetaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 4,
  fontSize: 12.5,
  color: 'var(--fg-3)',
};

const hintStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
};

export default CalendarTab;
